import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
// @mui
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
// components
import Iconify from "src/components/iconify";
//
import { isDateType } from "../core/format";

const valueKey = value =>
  value === null || value === undefined ? "\u0000null" : `${typeof value}:${String(value)}`;

function monthName(month, language) {
  return new Date(2000, month - 1, 1).toLocaleString(language, { month: "long" });
}

function dateLeaves(tree) {
  const leaves = [];

  tree.forEach(year => {
    if (!year.children) {
      leaves.push(year);
      return;
    }

    year.children.forEach(month => (month.children || []).forEach(day => leaves.push(day)));
  });

  return leaves;
}

function coveredBy(path, selectedPaths) {
  const parts = path.split("/");

  for (let length = 1; length <= parts.length; length += 1)
    if (selectedPaths.has(parts.slice(0, length).join("/"))) return true;

  return false;
}

function compressDateSelection(tree, selectedLeaves) {
  const values = [];

  tree.forEach(year => {
    if (!year.children) {
      if (selectedLeaves.has(year.value)) values.push(year.value);
      return;
    }

    const yearLeaves = year.children.flatMap(month => month.children || []);

    if (yearLeaves.length && yearLeaves.every(day => selectedLeaves.has(day.value))) {
      values.push(year.value);
      return;
    }

    (year.children || []).forEach(month => {
      const days = month.children || [];

      if (days.length && days.every(day => selectedLeaves.has(day.value))) {
        values.push(month.value);
        return;
      }

      days.forEach(day => {
        if (selectedLeaves.has(day.value)) values.push(day.value);
      });
    });
  });

  return values;
}

function DateTree({ tree, selected, onToggle, language, blankText }) {
  const [expanded, setExpanded] = useState(() => new Set());

  const stateOf = node => {
    const leaves = node.children
      ? node.children.flatMap(child => child.children || [child])
      : [node];
    const count = leaves.filter(leaf => selected.has(leaf.value)).length;

    return {
      checked: count > 0 && count === leaves.length,
      indeterminate: count > 0 && count < leaves.length,
      leaves,
    };
  };

  const toggleExpand = value =>
    setExpanded(current => {
      const next = new Set(current);

      if (next.has(value)) next.delete(value);
      else next.add(value);

      return next;
    });

  const renderNode = (node, depth) => {
    const { checked, indeterminate, leaves } = stateOf(node);
    const hasChildren = !!node.children?.length;
    let label = node.text;

    if (depth === 1) label = monthName(node.month, language);
    if (node.value === null) label = blankText;

    return (
      <Box key={node.value ?? "null"}>
        <Stack
          direction="row"
          alignItems="center"
          sx={{ pl: depth * 2.5, minHeight: 32 }}
        >
          {hasChildren ? (
            <IconButton
              size="small"
              onClick={() => toggleExpand(node.value)}
              sx={{ p: 0.25 }}
            >
              <Iconify
                icon={
                  expanded.has(node.value)
                    ? "eva:arrow-ios-downward-fill"
                    : "eva:arrow-ios-forward-fill"
                }
                width={16}
              />
            </IconButton>
          ) : (
            <Box sx={{ width: 24 }} />
          )}
          <Checkbox
            size="small"
            checked={checked}
            indeterminate={indeterminate}
            onChange={event =>
              onToggle(
                leaves.map(leaf => leaf.value),
                event.target.checked
              )
            }
            sx={{ p: 0.5 }}
          />
          <Typography
            variant="body2"
            noWrap
            sx={{ textTransform: depth === 1 ? "capitalize" : "none" }}
          >
            {label}
          </Typography>
        </Stack>
        {hasChildren && expanded.has(node.value)
          ? node.children.map(child => renderNode(child, depth + 1))
          : null}
      </Box>
    );
  };

  return <>{tree.map(node => renderNode(node, 0))}</>;
}

DateTree.propTypes = {
  tree: PropTypes.array,
  selected: PropTypes.object,
  onToggle: PropTypes.func,
  language: PropTypes.string,
  blankText: PropTypes.string,
};

export default function HeaderFilterPopover({ target, ctx, onClose, keepAllSelected }) {
  const column = target?.column;
  const open = !!target;
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const isDate = !!column && isDateType(column.dataType) && !column.headerFilterDataSource;
  const allowSearch = column?.headerFilterAllowSearch ?? ctx.headerFilterAllowSearch ?? true;

  useEffect(() => {
    if (!open || !column) return undefined;

    let active = true;

    setItems(null);
    setError(null);
    setSearch("");

    ctx
      .loadHeaderFilterValues(column)
      .then(result => {
        if (!active) return;

        const condition = ctx.getHeaderFilterCondition(column);
        const conditionValues = condition?.values || [];
        const next = new Set();

        if (isDate) {
          const leaves = dateLeaves(result);
          const paths = new Set(conditionValues.map(value => String(value)));

          leaves.forEach(leaf => {
            const isSelected = coveredBy(String(leaf.value), paths);

            if (condition?.type === "noneof" ? !isSelected : isSelected) next.add(leaf.value);
          });
        } else {
          const keys = new Set(conditionValues.map(valueKey));

          result.forEach(item => {
            const isSelected = keys.has(valueKey(item.value));

            if (condition?.type === "noneof" ? !isSelected : isSelected)
              next.add(valueKey(item.value));
          });
        }

        setSelected(next);
        setItems(result);
      })
      .catch(loadError => {
        if (active) setError(loadError);
      });

    return () => {
      active = false;
    };
  }, [open, column, ctx, isDate]);

  const visibleItems = useMemo(() => {
    if (!items || isDate) return items;
    if (!search.trim()) return items;

    const term = search.trim().toLowerCase();

    return items.filter(item => String(item.text).toLowerCase().includes(term));
  }, [items, isDate, search]);

  const toggleValues = (values, checked) =>
    setSelected(current => {
      const next = new Set(current);

      values.forEach(value => {
        if (checked) next.add(value);
        else next.delete(value);
      });

      return next;
    });

  const allKeys = useMemo(() => {
    if (!visibleItems) return [];
    if (isDate) return dateLeaves(visibleItems).map(leaf => leaf.value);

    return visibleItems.map(item => valueKey(item.value));
  }, [isDate, visibleItems]);

  const selectedCount = allKeys.filter(key => selected.has(key)).length;
  const allSelected = allKeys.length > 0 && selectedCount === allKeys.length;

  const handleApply = () => {
    if (!items) return;

    if (isDate) {
      const leaves = dateLeaves(items);
      const everything = leaves.length > 0 && leaves.every(leaf => selected.has(leaf.value));

      ctx.applyHeaderFilter(
        column,
        "anyof",
        everything && !keepAllSelected ? [] : compressDateSelection(items, selected)
      );
    } else {
      const values = items
        .filter(item => selected.has(valueKey(item.value)))
        .map(item => item.value);
      const everything = values.length === items.length && !search.trim();

      ctx.applyHeaderFilter(column, "anyof", everything && !keepAllSelected ? [] : values);
    }

    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={target?.anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{ paper: { sx: { width: 300, maxWidth: "calc(100vw - 32px)" } } }}
    >
      <Stack sx={{ p: 1.5, pb: 1 }}>
        <Typography
          variant="subtitle2"
          noWrap
          sx={{ mb: 1 }}
        >
          {column?.caption}
        </Typography>
        {allowSearch && !isDate ? (
          <InputBase
            autoFocus
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder={ctx.t("gridSearch")}
            startAdornment={
              <Iconify
                icon="eva:search-fill"
                width={18}
                sx={{ color: "text.disabled", mr: 1 }}
              />
            }
            sx={{
              px: 1,
              mb: 1,
              height: 36,
              borderRadius: 1,
              bgcolor: theme => theme.palette.action.hover,
            }}
          />
        ) : null}
        <Stack
          direction="row"
          alignItems="center"
          sx={{ pl: isDate ? 0 : 0.5 }}
        >
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={selectedCount > 0 && !allSelected}
            disabled={!allKeys.length}
            onChange={event => toggleValues(allKeys, event.target.checked)}
            sx={{ p: 0.5, ml: isDate ? 3 : 0 }}
          />
          <Typography variant="body2">{ctx.t("gridSelectAll")}</Typography>
        </Stack>
      </Stack>
      <Divider />
      <Box sx={{ maxHeight: 300, overflowY: "auto", px: 1.5, py: 0.5 }}>
        {!items && !error ? (
          <Stack
            alignItems="center"
            sx={{ py: 3 }}
          >
            <CircularProgress size={24} />
          </Stack>
        ) : null}
        {error ? (
          <Typography
            variant="body2"
            color="error"
            sx={{ py: 2 }}
          >
            {ctx.t("gridLoadError")}
          </Typography>
        ) : null}
        {items && isDate ? (
          <DateTree
            tree={items}
            selected={selected}
            onToggle={toggleValues}
            language={ctx.language}
            blankText={ctx.t("gridBlank")}
          />
        ) : null}
        {items && !isDate
          ? visibleItems.map(item => {
              const key = valueKey(item.value);

              return (
                <Stack
                  key={key}
                  direction="row"
                  alignItems="center"
                  component="label"
                  sx={{ minHeight: 32, cursor: "pointer", pl: 0.5 }}
                >
                  <Checkbox
                    size="small"
                    checked={selected.has(key)}
                    onChange={event => toggleValues([key], event.target.checked)}
                    sx={{ p: 0.5 }}
                  />
                  <Typography
                    variant="body2"
                    noWrap
                    title={item.text}
                    sx={{ color: item.value == null ? "text.secondary" : "text.primary" }}
                  >
                    {item.text}
                  </Typography>
                </Stack>
              );
            })
          : null}
        {items && !isDate && visibleItems.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ py: 2, color: "text.disabled", textAlign: "center" }}
          >
            {ctx.t("gridNoData")}
          </Typography>
        ) : null}
      </Box>
      <Divider />
      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        sx={{ p: 1 }}
      >
        <Button
          size="small"
          color="inherit"
          onClick={onClose}
        >
          {ctx.t("cancel")}
        </Button>
        <Button
          size="small"
          variant="soft"
          color="primary"
          disabled={!items}
          onClick={handleApply}
        >
          {ctx.t("gridApply")}
        </Button>
      </Stack>
    </Popover>
  );
}

HeaderFilterPopover.propTypes = {
  target: PropTypes.object,
  ctx: PropTypes.object,
  onClose: PropTypes.func,
  keepAllSelected: PropTypes.bool,
};
